import {
  doc,
  setDoc,
  getDocFromServer,
  WriteBatch,
  Transaction,
  UpdateData,
  DocumentSnapshot,
} from 'firebase/firestore'
import { FirebaseError } from 'firebase/app'
import { getStorage, ref, uploadBytes, getMetadata } from 'firebase/storage'
import firestore from '../../firestore'
import { Template } from '@leadgen/models/client'
import config from '../../config'
import BaseService from '../BaseService'
import { defaultTemplateId } from '../../constants/defaultTemplateId'

class TemplateService extends BaseService {
  private _previewUrlCache: Promise<Cache> | undefined
  private get previewUrlCache() {
    if (!this._previewUrlCache) {
      this._previewUrlCache = caches.open('template-preview-url-cache')
    }
    return this._previewUrlCache
  }
  previewUrlLocalCache: { [key: string]: Promise<string> | undefined } = {}
  fetchTemplates = async ({
    companyId,
    onTemplateAdded,
    onTemplateUpdated,
  }: {
    companyId: string
    onTemplateAdded: (template: Template) => void
    onTemplateUpdated: (template: Template) => void
  }) => {
    const { data, unsubscribe } = await this.onSnapshot({
      query: firestore.templates(companyId),
      onAdded: onTemplateAdded,
      onUpdated: onTemplateUpdated,
    })
    return {
      data,
      unsubscribe,
    }
  }
  fetchTemplate = async ({
    companyId,
    transaction,
  }: {
    transaction?: Transaction
    companyId: string
  }): Promise<Template> => {
    const templateRef = doc(firestore.templates(companyId), defaultTemplateId)
    let templateDoc: DocumentSnapshot
    if (transaction) {
      templateDoc = await transaction.get(templateRef)
    } else {
      templateDoc = await getDocFromServer(templateRef)
    }
    if (!templateDoc.exists()) {
      throw new Error("Template doesn't exist")
    }
    return templateDoc.data() as Template
  }
  subscribeToTemplate = async ({
    companyId,
    onTemplateUpdated,
    onTemplateAdded,
  }: {
    onTemplateUpdated?: (template: Template) => void
    onTemplateAdded?: (template: Template) => void
    companyId: string
  }) => {
    const { data, unsubscribe } = await this.onDoc({
      ref: doc(firestore.templates(companyId), defaultTemplateId),
      onUpdated: onTemplateUpdated,
      onAdded: onTemplateAdded,
    })
    return {
      data,
      unsubscribe,
    }
  }
  updateTemplateVersion = ({
    transactionOrBatch,
    version,
    companyId,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
    version: number
  }): void | Promise<void> => {
    const url = `${config.bucketBaseUrl}${encodeURIComponent(`templates/${companyId}/${defaultTemplateId}_${version}.pdf`)}?alt=media`
    const templateJSONUrl = `${config.bucketBaseUrl}${encodeURIComponent(`templates/${companyId}/${defaultTemplateId}_${version}.json`)}?alt=media`
    const templatesRef = firestore.templates(companyId)
    const templateRef = doc(templatesRef, defaultTemplateId)
    const templateUpdate: UpdateData<Template> = {
      version,
      generatingPreview: true,
      approved: false,
      url,
      templateJSONUrl,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(templateRef, templateUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(templateRef, templateUpdate)
      }
      return
    }
    return setDoc(templateRef, templateUpdate, { merge: true })
  }
  approveTemplate = ({
    transactionOrBatch,
    companyId,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
  }): void | Promise<void> => {
    const templatesRef = firestore.templates(companyId)
    const templateRef = doc(templatesRef, defaultTemplateId)
    const templateUpdate: UpdateData<Template> = {
      approved: true,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(templateRef, templateUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(templateRef, templateUpdate)
      }
      return
    }
    return setDoc(templateRef, templateUpdate, { merge: true })
  }
  createTemplate = ({
    transactionOrBatch,
    template,
    companyId,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    template: Omit<Template, 'id'>
    companyId: string
  }): Template | Promise<Template> => {
    const templatesRef = firestore.templates(companyId)
    const templateRef = doc(templatesRef, defaultTemplateId)
    const completeTemplate: Template = {
      ...template,
      id: templateRef.id,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.set(templateRef, completeTemplate, { merge: false })
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.set(templateRef, completeTemplate, { merge: false })
      }
      return completeTemplate
    }
    return setDoc(templateRef, completeTemplate, { merge: false }).then(() => {
      return completeTemplate
    })
  }
  uploadPdf = async ({
    pdf,
    companyId,
    version,
  }: {
    pdf: Blob
    companyId: string
    version: number
  }): Promise<string> => {
    const storage = getStorage()
    const storageRef = ref(storage, `templates/${companyId}/${defaultTemplateId}_${version}.pdf`)
    await uploadBytes(storageRef, pdf, {
      customMetadata: { version: version.toString() },
      cacheControl: 'public, max-age=3600',
    }).catch((error: Error) => {
      throw new Error(`Failed to upload pdf to the cloud. Error: ${error.message}`)
    })
    return `${config.bucketBaseUrl}${encodeURIComponent(storageRef.fullPath)}?alt=media`
  }
  uploadTemplateJSON = async ({
    templateJSON,
    companyId,
    version,
  }: {
    templateJSON: Blob
    companyId: string
    version: number
  }): Promise<string> => {
    const storage = getStorage()
    const storageRef = ref(storage, `templates/${companyId}/${defaultTemplateId}_${version}.json`)
    await uploadBytes(storageRef, templateJSON, {
      customMetadata: { version: version.toString() },
      contentType: 'application/json',
    }).catch((error: Error) => {
      throw new Error(`Failed to upload annotations to the cloud. Error: ${error.message}`)
    })
    return `${config.bucketBaseUrl}${encodeURIComponent(storageRef.fullPath)}?alt=media`
  }
  fetchPreviewUrl = async ({
    companyId,
    templateVersion,
  }: {
    companyId: string
    templateVersion: number
  }) => {
    // const previewUrl = `${config.imgixBaseUrl}previews/${companyId}/${defaultTemplateId}_${templateVersion}.pdf?page=1&fm=png&auto=format&fit=max&w=${imageDimensions.width}&h=${imageDimensions.height}`
    const previewUrl = `${config.bucketBaseUrl}previews${encodeURIComponent(`/${companyId}/${defaultTemplateId}_${templateVersion}.pdf`)}?alt=media`
    const filePath = `previews/${companyId}/${defaultTemplateId}_${templateVersion}.pdf`
    if (this.previewUrlLocalCache[filePath]) {
      return this.previewUrlLocalCache[filePath]
    }
    const defered = Promise.withResolvers<string>()
    this.previewUrlLocalCache[filePath] = defered.promise
    const previewUrlCache = await this.previewUrlCache
    const cachedResponse = await previewUrlCache.match(filePath)
    if (cachedResponse) {
      defered.resolve(previewUrl)
      return previewUrl
    }
    const storage = getStorage()
    const storageRef = ref(storage, filePath)
    const previewExists = getMetadata(storageRef)
      .then(async () => {
        await previewUrlCache.put(filePath, new Response())
        return true
      })
      .catch((error: FirebaseError) => {
        if (error.code === 'storage/object-not-found') {
          return false
        }
        delete this.previewUrlLocalCache[filePath]
        throw error
      })
    if (!previewExists) {
      delete this.previewUrlLocalCache[filePath]
      return null
    }
    defered.resolve(previewUrl)
    return previewUrl
  }
}

export default new TemplateService()
